import React, { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail && detail.msg) return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onOpenChange, initialMode = "signin" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => { if (open) { setMode(initialMode); setErr(""); } }, [open, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "signin") await login(email, password);
      else await register(email, password, name);
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-[var(--uc-bubble)] border border-[var(--uc-border)] rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-[var(--uc-muted)] focus:outline-none focus:border-[var(--uc-border-active)] transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15112A] border border-[var(--uc-border)] rounded-2xl max-w-sm p-0 gap-0 text-white" data-testid="auth-modal">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <img src="/uc-box-purple.png" alt="unboxcases" className="h-8 w-auto object-contain" draggable={false} />
            <span className="font-display text-xl font-extrabold lowercase tracking-tight text-white">unbox<span className="bg-gradient-to-r from-[#A78BFA] to-[#7C3AED] bg-clip-text text-transparent">cases</span></span>
          </div>
          <h2 className="font-cases text-lg uppercase tracking-tight mt-3 text-white">{mode === "signin" ? "Sign in" : "Create account"}</h2>
          <p className="text-xs text-[var(--uc-muted)] mb-4">{mode === "signin" ? "Welcome back — sign in to keep unboxing." : "Join unboxcases to save your unboxings."}</p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input data-testid="auth-name" className={inputCls} placeholder="Display name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <input data-testid="auth-email" type="email" required className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="auth-password" type="password" required className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            {err && <div data-testid="auth-error" className="text-xs text-[#FDA4CE] bg-[#3a2230] border border-[#5a2b42] rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="auth-submit" disabled={busy} type="submit"
              className="w-full py-2.5 rounded-xl font-cases uppercase tracking-wide text-sm bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white hover:from-[#7C3AED] hover:to-[#5B21B6] shadow-lg shadow-purple-900/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "signin" ? "Sign in" : "Sign up"}
            </button>
          </form>

          <div className="text-center text-xs text-[var(--uc-muted)] mt-4">
            {mode === "signin" ? "New here? " : "Already have an account? "}
            <button data-testid="auth-toggle" onClick={() => { setErr(""); setMode(mode === "signin" ? "signup" : "signin"); }}
              className="font-semibold text-[#C4B5FD] underline underline-offset-2 hover:text-white">
              {mode === "signin" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
